/**
 * Educational Article Card Component
 * Carbon Design System tile component for displaying educational content
 * Includes SEO metrics, analytics, and accessibility features
 */

import React, { useState } from 'react';
import { ClickableTile, Tag, Button, OverflowMenu, OverflowMenuItem, Stack, Layer, Tooltip } from '@carbon/react';
import {
  View,
  Edit,
  TrashCan,
  Analytics,
  Launch,
  Share,
  Bookmark,
  Time,
  Growth,
  User,
  Language,
  Search,
  UserMultiple,
  Education,
  UserAdmin,
} from '@carbon/icons-react';
import styles from './ArticleCard.module.scss';

interface EducationalContent {
  id: string;
  type: 'testimonial' | 'success-story' | 'demo-experience' | 'learning-outcome' | 'article' | 'guide';
  language: 'en' | 'pt';
  variant?: string;
  title: string;
  description: string;
  content: any;
  metadata?: {
    author?: string;
    institution?: string;
    tags?: string[];
    publishedAt?: string;
    updatedAt?: string;
    readingTime?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    targetAudience?: 'parents' | 'educators' | 'administrators' | 'all';
  };
  seoData?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
    canonicalUrl?: string;
    ogImage?: string;
    structuredData?: any;
  };
  performance?: {
    views: number;
    shares: number;
    engagementRate: number;
    conversionRate: number;
    seoRanking?: number;
  };
}

interface ArticleCardProps {
  content: EducationalContent;
  onAction?: (action: 'view' | 'edit' | 'delete' | 'share' | 'bookmark' | 'analyze') => void;
  enableSEOOptimization?: boolean;
  enableAnalytics?: boolean;
  showMetrics?: boolean;
  variant?: 'default' | 'compact' | 'featured';
}

export const ArticleCard: React.FC<ArticleCardProps> = ({
  content,
  onAction,
  enableSEOOptimization = true,
  enableAnalytics = true,
  showMetrics = true,
  variant = 'default',
}) => {
  const [isBookmarked, setIsBookmarked] = useState(false);

  const getTypeColor = (type: string) => {
    // Using Carbon semantic color types for tags
    switch (type) {
      case 'article':
        return 'blue';
      case 'guide':
        return 'green';
      case 'testimonial':
        return 'purple';
      case 'success-story':
        return 'teal';
      case 'demo-experience':
        return 'cyan';
      case 'learning-outcome':
        return 'magenta';
      default:
        return 'gray';
    }
  };

  const getDifficultyColor = (difficulty?: string) => {
    // Using Carbon semantic color types for tags
    switch (difficulty) {
      case 'beginner':
        return 'green';
      case 'intermediate':
        return 'blue';
      case 'advanced':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getAudienceIcon = (audience?: string) => {
    switch (audience) {
      case 'parents':
        return UserMultiple;
      case 'educators':
        return Education;
      case 'administrators':
        return UserAdmin;
      default:
        return UserMultiple;
    }
  };

  const getSEOScore = () => {
    let score = 0;
    const seo = content.seoData;

    // Basic SEO checks
    if (seo?.metaTitle && seo.metaTitle.length >= 30 && seo.metaTitle.length <= 60) score += 20;
    if (seo?.metaDescription && seo.metaDescription.length >= 120 && seo.metaDescription.length <= 160) score += 20;
    if (seo?.keywords && seo.keywords.length >= 3) score += 15;
    if (content.title && content.title.length >= 30 && content.title.length <= 60) score += 15;
    if (content.description && content.description.length >= 120) score += 15;
    if (content.metadata?.tags && content.metadata.tags.length >= 3) score += 15;

    return Math.min(score, 100);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Draft';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatNumber = (num?: number) => {
    if (!num) return '0';
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  const handleAction = (action: string) => {
    if (action === 'bookmark') {
      setIsBookmarked(!isBookmarked);
    }
    onAction?.(action as any);
  };

  const seoScore = enableSEOOptimization ? getSEOScore() : null;
  const performance = content.performance;

  return (
    <div className={`${styles.articleCard} ${styles[variant]}`}>
      <ClickableTile className={styles.tile} onClick={() => handleAction('view')} aria-label={`View ${content.title}`}>
        <div className={styles.content}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.metadata}>
              <Tag type={getTypeColor(content.type)} size="sm">
                {content.type.replace('-', ' ')}
              </Tag>
              <Tag type={content.language === 'en' ? 'blue' : 'green'} size="sm">
                {content.language.toUpperCase()}
              </Tag>
              {content.metadata?.difficulty && (
                <Tag type={getDifficultyColor(content.metadata.difficulty)} size="sm">
                  {content.metadata.difficulty}
                </Tag>
              )}
            </div>

            <div className={styles.actions}>
              <Tooltip content={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Bookmark}
                  iconDescription="Bookmark"
                  onClick={e => {
                    e.stopPropagation();
                    handleAction('bookmark');
                  }}
                  className={isBookmarked ? styles.bookmarked : ''}
                />
              </Tooltip>

              <OverflowMenu flipped iconDescription="More actions" onClick={e => e.stopPropagation()}>
                <OverflowMenuItem itemText="Edit Content" onClick={() => handleAction('edit')} />
                <OverflowMenuItem itemText="Share Content" onClick={() => handleAction('share')} />
                {enableAnalytics && (
                  <OverflowMenuItem itemText="View Analytics" onClick={() => handleAction('analyze')} />
                )}
                <OverflowMenuItem itemText="Delete Content" isDelete onClick={() => handleAction('delete')} />
              </OverflowMenu>
            </div>
          </div>

          {/* Title and Description */}
          <div className={styles.textContent}>
            <h3 className="cds--type-heading-03">{content.title}</h3>
            <p className="cds--type-body-long-01">{content.description}</p>
          </div>

          {/* Tags */}
          {content.metadata?.tags && content.metadata.tags.length > 0 && (
            <div className={styles.tags}>
              {content.metadata.tags.slice(0, 3).map((tag, index) => (
                <Tag key={index} type="outline" size="sm">
                  {tag}
                </Tag>
              ))}
              {content.metadata.tags.length > 3 && (
                <span className="cds--type-helper-text-01">+{content.metadata.tags.length - 3} more</span>
              )}
            </div>
          )}

          {/* Metrics */}
          {showMetrics && (
            <div className={styles.metrics}>
              <Stack orientation="horizontal" gap={4}>
                {/* Performance Metrics */}
                {performance && (
                  <>
                    <div className={styles.metric}>
                      <View size={16} />
                      <span className="cds--type-helper-text-01">{formatNumber(performance.views)}</span>
                    </div>
                    <div className={styles.metric}>
                      <Share size={16} />
                      <span className="cds--type-helper-text-01">{formatNumber(performance.shares)}</span>
                    </div>
                    <div className={styles.metric}>
                      <Growth size={16} />
                      <span className="cds--type-helper-text-01">{performance.engagementRate.toFixed(1)}%</span>
                    </div>
                  </>
                )}

                {/* SEO Score */}
                {enableSEOOptimization && seoScore !== null && (
                  <div className={styles.metric}>
                    <Search size={16} />
                    <span
                      className={`cds--type-helper-text-01 ${styles.seoScore} ${
                        seoScore >= 80 ? styles.good : seoScore >= 60 ? styles.fair : styles.poor
                      }`}
                    >
                      SEO: {seoScore}%
                    </span>
                  </div>
                )}

                {/* Reading Time */}
                {content.metadata?.readingTime && (
                  <div className={styles.metric}>
                    <Time size={16} />
                    <span className="cds--type-helper-text-01">{content.metadata.readingTime} min</span>
                  </div>
                )}
              </Stack>
            </div>
          )}

          {/* Footer */}
          <div className={styles.footer}>
            <div className={styles.author}>
              {content.metadata?.targetAudience && (
                <span className={styles.audience}>
                  {React.createElement(getAudienceIcon(content.metadata.targetAudience), { size: 16 })}
                  <span className="cds--type-label-01">{content.metadata.targetAudience}</span>
                </span>
              )}
              {content.metadata?.author && (
                <span className={styles.authorName}>
                  <User size={16} />
                  <span className="cds--type-label-01">{content.metadata.author}</span>
                </span>
              )}
            </div>
            <span className="cds--type-helper-text-01">{formatDate(content.metadata?.publishedAt)}</span>
          </div>

          {/* SEO Status Indicator */}
          {enableSEOOptimization && seoScore !== null && (
            <div
              className={`${styles.seoIndicator} ${
                seoScore >= 80
                  ? styles.excellent
                  : seoScore >= 60
                    ? styles.good
                    : seoScore >= 40
                      ? styles.fair
                      : styles.poor
              }`}
            >
              <div className={styles.seoBar} style={{ width: `${seoScore}%` }} />
            </div>
          )}
        </div>
      </ClickableTile>
    </div>
  );
};

export default ArticleCard;
